import { isArray } from 'lodash'
import Swal from 'sweetalert2';
import { ElMessage } from 'element-plus'
import { userDataStorageKey } from '../../config'
import { bus } from '../eventbus'

// URLs that participate in the metadata-cache contract: backend honours
// X-Client-Start-Timestamp / X-Client-Language / X-Client-App-Skin headers
// and returns null'd blobs when the client cache is still valid.

function shouldAttachCacheHeaders(url) {
  if (!url) return false
  if (url.endsWith('/auth/signup')
    || url.endsWith('/auth/token/issue')
    || url.endsWith('/auth/me')
    || url.endsWith('/settings/account/undefined')) return true
  return false
}

// Read cached user-data directly from localStorage — the durable cache survives
// logout (Vuex state.user is wiped on logout to prevent leaking the prior
// user's data into the login screen, but localStorage is intentionally kept so
// the next login can present a warm cache).
function getCachedUser() {
  try {
    const raw = localStorage.getItem(userDataStorageKey)
    if (!raw) return {}
    return JSON.parse(raw) || {}
  } catch (e) {
    return {}
  }
}

// Lightweight translate for this plain-JS module (no Vue `this`). Mirrors the
// global mixin's translate(): consult the tenant translation map (keyed by the
// English string) from cached metadata first, else return the English literal.
// The app currently runs in English; Stage 5 adds the Bulgarian rows.
function tr(str) {
  try {
    const meta = getCachedUser().metadata
    const map = meta && meta.translations
    if (map && map[str]) return map[str]
  } catch (e) { /* fall through */ }
  return str
}

// One-line telemetry (free->paid intent), matching the tierGuard mixin.
function logTierEvent(event, payload) {
  try {
    // eslint-disable-next-line no-console
    console.info('[tier]', event, payload || {})
  } catch (e) { /* never let telemetry break UX */ }
}

// Map a 402 `limit` key to its human label (mirrors tierGuard.limitLabel). Multi-word
// keys (e.g. activeOrders) need an explicit label so the translation lookup resolves;
// single-word keys fall back to capitalize().
function limitLabel(limit) {
  switch (limit) {
    case 'users': return 'Users'
    case 'workcenters': return 'Workcenters'
    case 'workers': return 'Workers'
    case 'documents': return 'Documents'
    case 'storage': return 'Storage'
    case 'activeOrders': return 'Active orders'
    default: return capitalize(limit)
  }
}

// Build the localized "you hit your {limit} limit ({current}/{max})" line.
function formatLimitMessage(body) {
  const limit = (body && body.limit) || 'plan'
  const current = body && body.current != null ? body.current : ''
  const max = body && body.max != null ? body.max : ''
  return tr("You've reached your {0} limit ({1}/{2}) on the Always Free plan. Upgrade to add more.")
    .replace('{0}', tr(limitLabel(limit)))
    .replace('{1}', current)
    .replace('{2}', max)
}

function capitalize(s) {
  if (!s) return s
  return String(s).charAt(0).toUpperCase() + String(s).slice(1)
}

// Self-signup admin sees the upgrade dialog with a mailto CTA.
function showUpgradeDialog(body) {
  logTierEvent('upgrade_prompt_shown', body || {})
  Swal.fire({
    icon: 'info',
    title: tr('Upgrade your plan'),
    html: formatLimitMessage(body),
    showCancelButton: true,
    confirmButtonText: tr('Contact us to upgrade'),
    cancelButtonText: tr('Not now'),
  }).then((result) => {
    if (result && result.isConfirmed) {
      logTierEvent('upgrade_clicked', body || {})
      // Open the in-app upgrade-request form (mounted at the app root). The
      // dialog seeds its counts from the tenant's current usage. Replaces the
      // old mailto: CTA.
      bus.emit('open-upgrade-request')
    }
  })
}

export default (http, store, router) => {
  // https://github.com/axios/axios#interceptors
  http.interceptors.request.use((config) => {
    if (!shouldAttachCacheHeaders(config.url)) {
      return config
    }
    const user = getCachedUser()
    const metadata = user.metadata || {}
    const startTimestamp = user.startTimestamp
    const hasTranslations = metadata.translations && Object.keys(metadata.translations).length > 0
    const language = hasTranslations ? metadata.translationsLanguage : null
    const appSkinName = metadata.appSkin && metadata.appSkin.name
    const hasStatuses = metadata.statuses && Object.keys(metadata.statuses).length > 0

    config.headers = config.headers || {}
    if (typeof startTimestamp === 'number') {
      config.headers['X-Client-Start-Timestamp'] = String(startTimestamp)
    }
    if (language) {
      config.headers['X-Client-Language'] = language
    }
    if (appSkinName) {
      config.headers['X-Client-App-Skin'] = appSkinName
    }
    if (hasStatuses) {
      config.headers['X-Client-Statuses'] = String(hasStatuses)
    }
    return config
  })

  http.interceptors.response.use(
    (response) => response,
    /**
     * This is a central point to handle all
     * error messages generated by HTTP
     * requests
     */
    (error) => {
      const { response } = error

      if(!response) {
        // console.log(error.toString())
        store.dispatch('setFetching', { fetching: false })
        return Promise.reject(error)
      }
      /**
       * If token is either expired, not provided or invalid
       * then redirect to login. On server side the error
       * messages can be changed on app/Providers/EventServiceProvider.php
       */
      if ([401 /*, 400*/ , 403].indexOf(response.status) > -1) {
        router.push({ name: 'auth.signin' }).catch(() => {})
      } else if (
        response.status === 402 &&
        response.data &&
        response.data.error === 'tier_limit_exceeded'
      ) {
        // Free-tier limit hit server-side. Self-signup admins get the upgrade
        // dialog (prefilled from the body); everyone else gets a plain note to
        // contact their administrator. Either way the promise still rejects so
        // the caller can stop its own success flow.
        if (store.getters.isSignupAdmin) {
          showUpgradeDialog(response.data)
        } else {
          ElMessage({
            type: 'warning',
            message: tr('Limit reached — contact your administrator.'),
          })
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: 'An error occurred',
          text: isArray(response.data)
            ? response.data.messages.join(', ')
            : response.data.error
              ? `(${response.data.error.code}) ${response.data.error.message}`
              : response.data.message || 'An unexpected error occurred',
          confirmButtonText: 'OK'
        });
      }

      store.dispatch('setFetching', { fetching: false })

      return Promise.reject(error)
    }
  )
}
